import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export interface ChartSerie {
    id: string;
    label: string;
    color: string;
    data: number[];         // normalized % values per category
    rawData?: number[];     // absolute values for tooltip
}

@Component({
    selector: 'app-chart-horizontal-bar',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="relative w-full" [style.height.px]="chartHeight">
            <canvas #chartCanvas></canvas>
        </div>
    `,
})
export class ChartHorizontalBar implements AfterViewInit, OnChanges, OnDestroy {

    @ViewChild('chartCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() categories: string[] = [];
    @Input() series: ChartSerie[] = [];
    @Input() activeSeries: string[] = [];
    @Input() stacked: boolean = false;

    @Output() chartReady = new EventEmitter<void>();

    private chart: Chart | null = null;

    get chartHeight(): number {
        const rows = this.categories.length;
        if (this.stacked) {
            // Stacked: 1 bar per category regardless of series count
            return Math.max(180, rows * 38 + 50);
        }
        const activeCnt = this.activeSeries.length || 1;
        return Math.max(200, rows * activeCnt * 32 + 60);
    }

    constructor(private zone: NgZone) { }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => this.buildChart());
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Si llegan categorías nuevas (de vacío a con datos), reconstruir el gráfico completo
        if (changes['categories']) {
            const prev = changes['categories'].previousValue as string[];
            const curr = changes['categories'].currentValue as string[];
            if (curr?.length > 0 && (prev?.length === 0 || !prev)) {
                this.zone.runOutsideAngular(() => {
                    this.chart?.destroy();
                    this.chart = null;
                    this.buildChart();
                });
                return;
            }
        }
        if (this.chart && (changes['series'] || changes['activeSeries'] || changes['categories'])) {
            this.zone.runOutsideAngular(() => this.updateChart());
        }
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private buildChart(): void {
        if (!this.canvasRef) return;
        const ctx = this.canvasRef.nativeElement.getContext('2d');
        if (!ctx) return;

        this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: this.categories,
                datasets: this.buildDatasets(),
            },
            options: this.buildOptions(),
            plugins: [this.getDataLabelsPlugin()],
        });

        this.chartReady.emit();
    }

    private updateChart(): void {
        if (!this.chart) return;
        this.chart.data.labels = this.categories;
        this.chart.data.datasets = this.buildDatasets();
        this.chart.update();
    }

    private buildDatasets() {
        const filtered = this.series.filter(s => this.activeSeries.includes(s.id));
        return filtered.map((s, idx) => ({
            label: s.label,
            data: s.data,
            backgroundColor: s.color + 'D9',
            borderColor: s.color,
            borderWidth: this.stacked ? 0 : 1.5,
            // Only round the last visible segment when stacked
            borderRadius: this.stacked
                ? (idx === filtered.length - 1 ? { topRight: 6, bottomRight: 6 } : 0)
                : 6,
            borderSkipped: false,
        }));
    }

    private buildOptions(): any {
        return {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 600, easing: 'easeInOutQuart' },
            plugins: {
                legend: { display: false },
                tooltip: {
                    mode: this.stacked ? 'index' : 'nearest',
                    callbacks: {
                        label: (ctx: any) => {
                            const serie = this.series.find(s => s.label === ctx.dataset.label);
                            const pct = ctx.parsed.x?.toFixed(0) ?? '0';
                            if (serie?.rawData) {
                                const abs = serie.rawData[ctx.dataIndex] ?? 0;
                                return ` ${ctx.dataset.label}: ${pct}%  (${abs} alumnos)`;
                            }
                            return ` ${ctx.dataset.label}: ${pct}%`;
                        },
                    },
                    backgroundColor: 'rgba(15,23,42,0.92)',
                    titleColor: '#f8fafc',
                    bodyColor: '#cbd5e1',
                    padding: 12,
                    cornerRadius: 10,
                    titleFont: { size: 12, weight: 'bold' },
                    bodyFont: { size: 12 },
                },
            },
            scales: {
                x: {
                    stacked: this.stacked,
                    min: 0,
                    max: 100,
                    ticks: {
                        callback: (v: any) => v + '%',
                        color: '#94a3b8',
                        font: { size: 11 },
                    },
                    grid: { color: 'rgba(0,0,0,0.06)' },
                    border: { display: false },
                },
                y: {
                    stacked: this.stacked,
                    ticks: {
                        color: '#475569',
                        font: { size: 12, weight: '600' },
                    },
                    grid: { display: false },
                    border: { display: false },
                },
            },
        };
    }

    private getDataLabelsPlugin(): any {
        return {
            id: 'customDataLabels',
            afterDatasetsDraw: (chart: any) => {
                const { ctx } = chart;
                chart.data.datasets.forEach((dataset: any, i: number) => {
                    const meta = chart.getDatasetMeta(i);
                    if (meta.hidden) return;
                    meta.data.forEach((element: any, index: number) => {
                        const data = dataset.data[index];
                        if (typeof data === 'number' && data > 0) {
                            const text = Math.round(data) + '%';
                            ctx.save();
                            ctx.font = 'bold 11px sans-serif';
                            ctx.textBaseline = 'middle';
                            
                            const isSmall = data < 6;
                            ctx.fillStyle = isSmall ? '#475569' : '#ffffff';
                            
                            if (isSmall) {
                                ctx.textAlign = 'left';
                                ctx.fillText(text, element.x + 4, element.y);
                            } else {
                                ctx.textAlign = 'center';
                                ctx.fillText(text, (element.base + element.x) / 2, element.y);
                            }
                            ctx.restore();
                        }
                    });
                });
            }
        };
    }
}
